<script lang="ts">
  import SEO from '$lib/components/SEO.svelte';
  import Newsletter from '$lib/components/Newsletter.svelte';

  const POSTS = [
    {
      slug: '3-invisible-leaks-costing-bgc-smes-10-hours-a-week',
      title: '3 Invisible Leaks Costing BGC SMEs 10+ Hours a Week',
      excerpt: "Is your team scaling headcount or complexity? Discover the 3 manual bottlenecks that stall growth for Philippine SMEs and how to automate them.",
      date: '2026-03-16',
      readTime: '7 min read',
      category: 'Efficiency'
    },
    {
      slug: 'how-ai-is-reclaiming-hours-for-bgc-brokers',
      title: 'BGC Real Estate: Stop the Viber Noise with AI',
      excerpt: "How luxury real estate brokers in Manila are using AI agents to filter tyre-kickers and automate lead qualification 24/7.",
      date: '2026-03-16',
      readTime: '5 min read',
      category: 'Operations'
    },
    {
      slug: 'how-modern-smes-using-ai-to-scale-operations',
      title: 'How Modern High-Growth SMEs are Using AI to Scale Operations',
      excerpt: "Discover how businesses in high-velocity hubs are leveraging AI automation to eliminate manual repetitive work and scale their operations globally.",
      date: '2026-03-12',
      readTime: '7 min read',
      category: 'Operations'
    },
    {
      slug: 'is-your-business-ready-for-ai-automation',
      title: 'How to Know If Your Business Is Ready for AI Automation',
      excerpt: "Not every business needs AI automation right now. Here are 6 signs you're ready — and 4 honest signs you're not — before you invest.",
      date: '2026-03-08',
      readTime: '9 min read',
      category: 'Strategy'
    },
    {
      slug: 'workflows-every-small-business-should-automate',
      title: '5 Workflows Every Small Business Should Automate First',
      excerpt: 'Client onboarding, invoicing, support, lead nurturing, reporting — the five workflows that cost small businesses the most time and money, and what automation actually looks like for each.',
      date: '2026-03-08',
      readTime: '9 min read',
      category: 'Automation'
    },
    {
      slug: 'what-is-business-process-automation',
      title: 'What Is Business Process Automation? A Plain-English Guide for SME Owners',
      excerpt: "You've heard the term — but what does BPA actually mean for a business your size? A plain-English breakdown of what automation is, what it covers, and why small businesses benefit the most.",
      date: '2026-03-08',
      readTime: '9 min read',
      category: 'Automation'
    },
    {
      slug: 'how-to-choose-automation-agency',
      title: 'How to Choose a Business Automation Agency (And Avoid Expensive Mistakes)',
      excerpt: "What separates a real automation partner from an expensive disappointment. A practical buyer's guide covering what to look for, what to run from, and the questions every business should ask before signing.",
      date: '2026-03-08',
      readTime: '9 min read',
      category: "Buyer's Guide"
    },
    {
      slug: 'signs-business-losing-money-manual-processes',
      title: '5 Signs Your Business Is Losing Money to Manual Processes',
      excerpt: 'Your team is busy — but is the work actually moving the business forward? Here are five warning signs that manual processes are quietly draining your revenue.',
      date: '2026-03-08',
      readTime: '7 min read',
      category: 'Operations'
    },
    {
      slug: 'what-happens-when-you-automate-onboarding',
      title: 'What Actually Happens When You Automate Your Client Onboarding',
      excerpt: 'The difference between manual onboarding chaos and an automated experience — told from the perspective of the businesses who made the switch.',
      date: '2026-03-01',
      readTime: '8 min read',
      category: 'Automation'
    },
    {
      slug: 'how-ecommerce-store-cut-support-tickets',
      title: 'How an E-Commerce Store Cut Support Tickets by 70%',
      excerpt: 'An anonymized case study: from 840 tickets per month to 250, with faster response times and happier customers.',
      date: '2026-02-20',
      readTime: '8 min read',
      category: 'Case Study'
    }
  ];
</script>

<SEO 
  title="Blog — Business Automation Insights — Bleunk"
  description="Insights on business automation, operational efficiency, and real results from companies that stopped doing everything manually. From the Bleunk team."
  canonical="https://bleunk.com/blog"
/>

<svelte:head>
  <meta name="keywords" content="business automation blog, automation case studies, operational efficiency, reduce manual processes, business process automation, automation ROI" />

  {@html `<script type="application/ld+json">${JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://bleunk.com/blog#webpage",
        "url": "https://bleunk.com/blog",
        "name": "Blog — Business Automation Insights — Bleunk",
        "description": "Insights on business automation, operational efficiency, and real results from companies that stopped doing everything manually. From the Bleunk team.",
        "isPartOf": { "@id": "https://bleunk.com/#website" },
        "inLanguage": "en-PH",
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", ".blog-intro"]
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bleunk.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://bleunk.com/blog" }
          ]
        }
      },
      {
        "@type": "Blog",
        "@id": "https://bleunk.com/blog#blog",
        "url": "https://bleunk.com/blog",
        "name": "Bleunk Blog",
        "description": "Expert guides on business automation, AI agents, and modern web development for Philippine SMEs and global businesses.",
        "publisher": { "@id": "https://bleunk.com/#organization" },
        "inLanguage": "en-PH",
        "blogPost": POSTS.map(post => ({
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "url": "https://bleunk.com/blog/" + post.slug,
          "datePublished": post.date,
          "author": {
            "@type": "Organization",
            "name": "Bleunk Technologies Inc.",
            "url": "https://bleunk.com"
          }
        }))
      },
      {
        "@type": "ItemList",
        "name": "Bleunk Blog Posts",
        "description": "Practical guides on business automation, AI chatbots, and web development",
        "numberOfItems": POSTS.length,
        "itemListElement": POSTS.map((post, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": post.title,
          "description": post.excerpt,
          "url": "https://bleunk.com/blog/" + post.slug
        }))
      }
    ]
  })}</script>`}
</svelte:head>

<section class="pt-32 pb-24 px-4">
  <div class="max-w-4xl mx-auto">
    <!-- Breadcrumb -->
    <nav class="flex items-center gap-2 text-xs text-dim-30 mb-8" aria-label="Breadcrumb">
      <a href="/" class="hover:text-dim-60 transition-colors">Home</a>
      <span>/</span>
      <span class="text-dim-50">Blog</span>
    </nav>

    <div class="text-center mb-16">
      <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dim-8 bg-dim-3 text-dim-50 text-xs font-medium mb-4">
        Blog
      </div>
      <h1 class="font-display text-4xl sm:text-5xl font-bold mb-4">
        Insights &<br /><span class="gradient-accent-text">Real Results</span>
      </h1>
      <p class="text-dim-50 max-w-xl mx-auto blog-intro">
        What happens when businesses stop doing everything manually. Case studies, operational insights, and the real cost of not automating.
      </p>
    </div>

    <div class="grid gap-6">
      {#each POSTS as post}
        <a 
          href="/blog/{post.slug}" 
          class="block p-6 rounded-2xl border border-dim-6 bg-card hover:border-[#C9A87C]/30 hover:-translate-y-1 transition-all duration-300"
        >
          <div class="flex items-center gap-3 text-xs text-dim-40 mb-3">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
            <span>·</span>
            <span class="text-[#C9A87C]">{post.category}</span>
          </div>
          <h2 class="font-display text-xl font-bold mb-2">{post.title}</h2>
          <p class="text-dim-50 text-sm">{post.excerpt}</p>
        </a>
      {/each}
    </div>

    <!-- Lead Magnet -->
    <div class="mt-24">
      <Newsletter />
    </div>
  </div>
</section>
